import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, HelpCircle, MessageCircle } from "lucide-react";
import AnimateOnScroll, { StaggerContainer, StaggerItem } from "@/components/AnimateOnScroll";
import { siteConfig } from "@/lib/data";
import { useTranslation } from "react-i18next";

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border border-white/[0.06] rounded-xl overflow-hidden hover:border-neon-500/20 transition-colors">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="text-sm font-medium text-white/80 pr-4">{q}</span>
        <ChevronDown
          className={`w-5 h-5 text-neon-400 flex-shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 text-sm text-white/50 leading-relaxed border-t border-white/[0.04] pt-4">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPage() {
  const { t } = useTranslation();
  return (
    <>
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-30" />
        <div className="section-container section-padding relative z-10">
          <AnimateOnScroll className="text-center max-w-3xl mx-auto">
            <span className="text-neon-400 font-mono text-sm tracking-widest uppercase">
              {t('home.faq_page.kategori')}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-6 gradient-text-white">
              {t('home.faq_page.title_main')}<span className="gradient-text">{t('home.faq_page.title_highlight')}</span>
            </h1>
            <p className="text-white/50 text-lg leading-relaxed">
              {t('home.faq_page.desc')}
            </p>
          </AnimateOnScroll>
        </div>
      </section>

      <section className="pb-24">
        <div className="section-container section-padding max-w-4xl mx-auto">
          <StaggerContainer className="space-y-10" staggerDelay={0.1}>
            {((t('home.faq_page.faqCategories', { returnObjects: true }) || []) as any[]).map((cat) => (
              <StaggerItem key={cat.title}>
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <HelpCircle className="w-5 h-5 text-neon-400" />
                    <h2 className="text-xl font-bold text-white">{cat.title}</h2>
                  </div>
                  <div className="space-y-2">
                    {cat.faqs.map((faq: any) => (
                      <FaqItem key={faq.q} q={faq.q} a={faq.a} />
                    ))}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <section className="pb-32 pt-10 relative z-10">
        <div className="section-container section-padding">
          <AnimateOnScroll>
            <div className="glass-card p-10 md:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-neon-500/5 to-transparent" />
              <div className="relative z-10">
                <MessageCircle className="w-10 h-10 text-neon-400 mx-auto mb-4" />
                <h2 className="text-2xl md:text-3xl font-bold gradient-text-white mb-4">
                  {t('home.faq_page.cta_title')}
                </h2>
                <p className="text-white/50 max-w-xl mx-auto mb-8">
                  {t('home.faq_page.cta_desc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/konsultasi" className="btn-primary">
                    {t('home.faq_page.btn_konsultasi')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <a
                    href={`https://wa.me/${siteConfig.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary"
                  >
                    {t('home.faq_page.btn_wa')}
                  </a>
                </div>
              </div>
            </div>
          </AnimateOnScroll>
        </div>
      </section>
    </>
  );
}
